import { request } from "../client"
import type { CostBenefitEstimate } from "../cost-benefit"
import type { ContactSequenceTemplateId } from "../planning"

export type ContactDestination = {
  kind: "EMAIL" | "PHONE"
  value: string
  contact_point_id?: string | null
  label?: string | null
}

export type ContactOrderStep = {
  id: string
  organization_id?: string
  contact_order_id?: string
  position: number
  channel: string
  execution_mode: "automatic_sequence" | "manual_letter_delivery" | "automatic_letter_delivery"
  automatic_delivery_committed: boolean
  destination?: ContactDestination | null
  due_at?: string | null
  status: ContactStepPhase
  title?: string | null
  notes?: string | null
  message_kind?: string | null
  message_label?: string | null
  template_key: "announcement" | "reschedule" | "follow_up"
  template_label?: string | null
  recipient_customer_id?: string | null
  recipient_role?: string | null
  letter_mode?: "personal" | "impersonal" | null
  postal_contact_point_id?: string | null
  delivery_due_at?: string | null
  subject_template_override?: string | null
  body_template_override?: string | null
  retries_on_no_answer?: number
  response_deadline_at?: string | null
  next_action_at?: string | null
  result_status?: ContactStepResult | null
  result_description?: string | null
  result_meta?: Record<string, unknown>
  result_recorded_at?: string | null
}

export type ContactOrder = {
  id: string
  organization_id: string
  case_id?: string | null
  work_order_id: string
  reason: ContactOrderReason
  status: ContactOrderPhase
  closed_reason?: ContactOrderClosedReason | null
  closed_reason_details?: string | null
  closed_by_type?: string | null
  closed_by_user_id?: string | null
  closed_by_label?: string | null
  schedule_event_id?: string | null
  proposed_start_at?: string | null
  proposed_end_at?: string | null
  assigned_employee_ids: string[]
  contact_sequence_template_id?: string | null
  last_result_status?: ContactStepResult | null
  last_result_description?: string | null
  last_result_at?: string | null
  last_result_step_id?: string | null
  steps?: ContactOrderStep[]
  created_at?: string | null
  updated_at?: string | null
}

export type ContactOrderCreatePayload = {
  planning_operation_id?: string
  work_order_id: string
  reason?: string
  schedule_event_id?: string | null
  previous_schedule_event_id?: string | null
  canceled_schedule_event_id?: string | null
  previous_appointment_start_at?: string | null
  previous_appointment_end_at?: string | null
  previous_schedule_event_status?: string | null
  replace_existing?: boolean
  assigned_employee_ids?: string[]
  proposed_start_at?: string | null
  proposed_end_at?: string | null
  contact_sequence_template_id?: ContactSequenceTemplateId | null
  communication_selection?: ContactOrderCommunicationSelection
}

export type ContactOrderDigitalChannel = "EMAIL" | "MOBILE_MESSAGE" | "PHONE"

export type ContactOrderDigitalRecipientSelection = {
  customer_id: string
  channels: ContactOrderDigitalChannel[]
}

export type AppointmentPostalAddress = {
  street: string
  house_number: string
  zip: string
  city: string
  country: string
  address_extra?: string | null
}
export type ContactOrderPostalSelection = {
  mode: "DISABLED" | "IMPERSONAL" | "PERSONAL" | "NOTICE"
  customer_id?: string | null
  address_source?: "CONTACT_POINT" | "OBJECT_ADDRESS" | null
  contact_point_id?: string | null
  recipient_name?: string | null
  recipient_revision?: string | null
  manual_recipient?: { name: string; address: AppointmentPostalAddress } | null
  dispatch_mode?: "MANUAL" | "AUTOMATIC" | null
}

export type ContactOrderCommunicationSelection = {
  digital: ContactOrderDigitalRecipientSelection[]
  postal: ContactOrderPostalSelection
}

export type ContactOrderBatchItemError = {
  index: number
  work_order_id?: string | null
  schedule_event_id?: string | null
  detail: string
}

export type ContactOrderBatchResponse = {
  created: ContactOrder[]
  failed: ContactOrderBatchItemError[]
}

export type ContactOrderBatchCancelItem = {
  case_id?: string | null
  contact_order_id: string
}

export type ContactOrderBatchCancelResponse = {
  canceled: ContactOrder[]
  skipped: Array<{ contact_order_id: string; reason?: string | null }>
  failed: Array<{ contact_order_id: string; detail: string }>
}

export type ContactOrderBatchCompleteResponse = {
  completed: ContactOrder[]
  skipped: Array<{ contact_order_id: string; reason?: string | null }>
  failed: Array<{ contact_order_id: string; detail: string }>
}

export type ContactOrderPlanningTag = {
  label: string
  color: string
  tooltip: string
  reason_code: string
  recipient_labels: string[]
  planned_step_count: number
}

export type ContactOrderPlanningRecipientSummary = {
  customer_id: string
  label: string
  role?: string | null
  role_label?: string | null
  color: string
  tooltip: string
  channel_labels: string[]
  planned_step_count: number
}

export type ContactOrderPlanningDigitalChannelTag = {
  channel: ContactOrderDigitalChannel
  customer_id: string
  recipient_label?: string | null
  role_label?: string | null
  qualifier_label?: string | null
  due_at?: string | null
  follow_up_due_at?: string | null
  planned: boolean
  reason_code: string
  tooltip: string
}

export type ContactOrderPlanningPostalOption = {
  mode: ContactOrderPostalSelection["mode"]
  customer_id?: string | null
  address_source?: "CONTACT_POINT" | "OBJECT_ADDRESS" | null
  contact_point_id?: string | null
  recipient_name?: string | null
  recipient_address?: AppointmentPostalAddress | null
  recipient_revision?: string | null
  label: string
  sub_label?: string | null
  available: boolean
  disabled_reason?: string | null
  tooltip?: string | null
}

export type ContactOrderCostBenefitContributionKey =
  | "EMAIL"
  | "MOBILE_MESSAGE"
  | "PHONE"
  | "CONFIRMATION_EMAIL"

export type ContactOrderCostBenefitContributionItem = {
  key: ContactOrderCostBenefitContributionKey
  label: string
  count: number
  expected_credits_basis_points: number
}

export type ContactOrderCostBenefitContribution = {
  time_saved_minutes: number
  remaining_effort_minutes_hundredths: number
  cost_items: ContactOrderCostBenefitContributionItem[]
}

export type ContactOrderPlanningPreview = {
  index: number
  case_id?: string | null
  work_order_id?: string | null
  schedule_event_id?: string | null
  contact_sequence_template_id?: string | null
  contact_sequence_template_label?: string | null
  contact_role_sequence_template_label?: string | null
  channel_tags: ContactOrderPlanningTag[]
  role_tags: ContactOrderPlanningTag[]
  recipient_summaries?: ContactOrderPlanningRecipientSummary[]
  digital_channel_tags?: ContactOrderPlanningDigitalChannelTag[]
  postal_options?: ContactOrderPlanningPostalOption[]
  postal_selection: ContactOrderPostalSelection
  postal_due_at?: string | null
  postal_recipient_name?: string | null
  postal_recipient_address?: AppointmentPostalAddress | null
  postal_dispatch_default?: "MANUAL" | "AUTOMATIC"
  automatic_postal_available?: boolean
  automatic_postal_disabled_reason?: string | null
  notice_available?: boolean
  customer_information_mode?: string | null
  customer_information_label?: string | null
  customer_information_detail?: string | null
  has_blockers: boolean
  cost_benefit_contribution?: ContactOrderCostBenefitContribution | null
}

export type ContactOrderBatchPreviewResponse = {
  previews: ContactOrderPlanningPreview[]
  failed: ContactOrderBatchItemError[]
  cost_benefit?: CostBenefitEstimate | null
}

const performanceNow = () =>
  typeof globalThis.performance?.now === "function" ? globalThis.performance.now() : Date.now()

const durationMs = (startedAt: number) => Math.round(performanceNow() - startedAt)

const logContactOrderBatchTiming = (event: string, fields: Record<string, unknown>) => {
  globalThis.console?.info?.(event, fields)
}

const stringValue = (value: unknown): string | null => {
  const text = typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim()

  return text || null
}

const templateRefValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const selected = value.map(stringValue).find((entry): entry is string => Boolean(entry))

    return selected || value
  }

  if (typeof value === "string") return stringValue(value) ?? undefined

  return value ?? undefined
}

const buildAppointmentPlanPayload = (payload: ContactOrderCreatePayload) => ({
  reason: payload.reason ?? "INITIAL",
  replace_existing: payload.replace_existing ?? false,
  proposed_start_at: payload.proposed_start_at ?? null,
  proposed_end_at: payload.proposed_end_at ?? null,
  assigned_actor_ids: payload.assigned_employee_ids ?? [],
  communication_selection: payload.communication_selection ?? undefined,
  planning_operation_id: payload.planning_operation_id,
  product_refs: {
    work_order_id: payload.work_order_id,
    schedule_event_id: payload.schedule_event_id ?? undefined,
    previous_schedule_event_id: payload.previous_schedule_event_id ?? undefined,
    canceled_schedule_event_id: payload.canceled_schedule_event_id ?? undefined,
    previous_appointment_start_at: payload.previous_appointment_start_at ?? undefined,
    previous_appointment_end_at: payload.previous_appointment_end_at ?? undefined,
    previous_schedule_event_status: payload.previous_schedule_event_status ?? undefined,
    contact_sequence_template_id: templateRefValue(payload.contact_sequence_template_id),
  },
})

export type ContactOrderStepEditPayload = {
  channel: ContactChannel
  execution_mode: ContactOrderStep["execution_mode"]
  automatic_delivery_committed: boolean
  contact_point_id?: string | null
  recipient_customer_id?: string | null
  letter_mode?: ContactOrderStep["letter_mode"]
  postal_contact_point_id?: string | null
  delivery_due_at?: string | null
  due_at: string
  title?: string | null
  notes?: string | null
  template_key: ContactOrderStep["template_key"]
  subject_template_override?: string | null
  body_template_override?: string | null
  retries_on_no_answer?: number
}

export type ContactOrderStepUpdatePayload = ContactOrderStepEditPayload
export type ContactOrderStepCreatePayload = ContactOrderStepEditPayload

export type ContactStepResultPayload = {
  status: string
  description?: string | null
  meta?: Record<string, unknown> | null
  responded_at?: string | null
}

export type ExecuteContactOrderStepNowPayload = {
  ignore_contact_window?: boolean
}

export type TestContactOrderStepPayload = {
  target_value: string
}

export type TestContactOrderStepResponse = {
  job_id: string
  step_id: string
  contact_order_id: string
  channel: ContactChannel
  queued_at?: string | null
  status: string
}

export type ContactOrderStepMutationResult = {
  order: ContactOrder
  step: ContactOrderStep
}

export const fetchContactOrders = (query: { caseId: string }) =>
  request<ContactOrder[]>(
    `/appointment-contact-plans?case_id=${encodeURIComponent(query.caseId)}`,
  ).then((orders) => orders)

export const createContactOrder = async (
  caseId: string,
  payload: ContactOrderCreatePayload,
): Promise<ContactOrder> => {
  const result = await createContactOrdersBatch([{ ...payload, case_id: caseId }])

  if (result.failed.length || result.created.length !== 1) {
    throw new Error(result.failed[0]?.detail || "Kontaktplan konnte nicht erstellt werden.")
  }

  return result.created[0]
}

export const createContactOrdersBatch = async (
  payload: Array<ContactOrderCreatePayload & { case_id?: string | null }>,
): Promise<ContactOrderBatchResponse> => {
  const startedAt = performanceNow()
  const failed: ContactOrderBatchItemError[] = []
  const items = payload
    .map((item, index) => {
      if (!item.case_id) {
        failed.push({
          index,
          work_order_id: item.work_order_id,
          schedule_event_id: item.schedule_event_id,
          detail: "Missing case_id",
        })

        return null
      }

      return {
        case_id: item.case_id,
        payload: buildAppointmentPlanPayload(item),
      }
    })
    .filter(
      (
        item,
      ): item is { case_id: string; payload: ReturnType<typeof buildAppointmentPlanPayload> } =>
        Boolean(item),
    )

  if (!items.length) {
    logContactOrderBatchTiming("contact_orders_batch_create_frontend_timing", {
      item_count: payload.length,
      request_item_count: 0,
      created_count: 0,
      failed_count: failed.length,
      skipped: true,
      duration_ms: durationMs(startedAt),
    })

    return { created: [], failed }
  }

  const response = await request<ContactOrderBatchResponse>("/appointment-contact-plans/batch", {
    method: "POST",
    body: JSON.stringify({ items }),
  })
  const result = {
    created: response.created ?? [],
    failed: [...failed, ...(response.failed ?? [])],
  }
  logContactOrderBatchTiming("contact_orders_batch_create_frontend_timing", {
    item_count: payload.length,
    request_item_count: items.length,
    created_count: result.created.length,
    failed_count: result.failed.length,
    duration_ms: durationMs(startedAt),
  })

  return result
}

export const previewContactOrdersBatch = async (
  payload: Array<ContactOrderCreatePayload & { case_id?: string | null }>,
): Promise<ContactOrderBatchPreviewResponse> => {
  const startedAt = performanceNow()
  const failed: ContactOrderBatchItemError[] = []
  const items = payload
    .map((item, index) => {
      if (!item.case_id) {
        failed.push({
          index,
          work_order_id: item.work_order_id,
          schedule_event_id: item.schedule_event_id,
          detail: "Missing case_id",
        })

        return null
      }

      return {
        case_id: item.case_id,
        payload: buildAppointmentPlanPayload(item),
      }
    })
    .filter(
      (
        item,
      ): item is { case_id: string; payload: ReturnType<typeof buildAppointmentPlanPayload> } =>
        Boolean(item),
    )

  if (!items.length) {
    logContactOrderBatchTiming("contact_orders_batch_preview_frontend_timing", {
      item_count: payload.length,
      request_item_count: 0,
      preview_count: 0,
      failed_count: failed.length,
      skipped: true,
      duration_ms: durationMs(startedAt),
    })

    return { previews: [], failed }
  }

  const response = await request<ContactOrderBatchPreviewResponse>(
    "/appointment-contact-plans/batch/preview",
    {
      method: "POST",
      body: JSON.stringify({ items }),
    },
  )
  const result = {
    previews: response.previews ?? [],
    failed: [...failed, ...(response.failed ?? [])],
    cost_benefit: response.cost_benefit ?? null,
  }
  logContactOrderBatchTiming("contact_orders_batch_preview_frontend_timing", {
    item_count: payload.length,
    request_item_count: items.length,
    preview_count: result.previews.length,
    failed_count: result.failed.length,
    duration_ms: durationMs(startedAt),
  })

  return result
}

const orderAndStepFromContactOrder = async (
  promise: Promise<ContactOrder>,
  stepId: string,
): Promise<ContactOrderStepMutationResult> => {
  const executionOrder = await promise
  const plans = await fetchContactOrders({ caseId: executionOrder.case_id! })
  const order = plans.find((plan) => plan.id === executionOrder.id)

  if (!order) throw new Error("Kontaktplan konnte nicht geladen werden.")

  const step = order.steps?.find((entry) => entry.id === stepId)

  if (!step) throw new Error("Ankündigungsschritt konnte nicht geladen werden.")

  return { order, step }
}

const stepFromContactOrder = async (promise: Promise<ContactOrder>, stepId: string) =>
  orderAndStepFromContactOrder(promise, stepId).then((result) => result.step)

export const updateContactOrderStep = (
  caseId: string,
  contactOrderId: string,
  stepId: string,
  payload: ContactOrderStepUpdatePayload,
) =>
  request<ContactOrderStep>(
    `/appointment-contact-plans/${contactOrderId}/steps/${stepId}?case_id=${caseId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  )

export const moveContactOrderStep = (
  caseId: string,
  contactOrderId: string,
  stepId: string,
  direction: "up" | "down",
) =>
  stepFromContactOrder(
    request<ContactOrder>(
      `/cases/${caseId}/contact-orders/${contactOrderId}/steps/${stepId}/move`,
      {
        method: "POST",
        body: JSON.stringify({ direction }),
      },
    ),
    stepId,
  )

export const deleteContactOrderStep = (caseId: string, contactOrderId: string, stepId: string) =>
  request<ContactOrder>(`/cases/${caseId}/contact-orders/${contactOrderId}/steps/${stepId}`, {
    method: "DELETE",
  }).then(() => undefined)

export const createContactOrderStep = (
  caseId: string,
  contactOrderId: string,
  payload: ContactOrderStepCreatePayload,
) =>
  request<ContactOrderStep>(
    `/appointment-contact-plans/${contactOrderId}/steps?case_id=${caseId}`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  )

export const reportContactStepResult = (
  caseId: string,
  contactOrderId: string,
  stepId: string,
  payload: ContactStepResultPayload,
) =>
  stepFromContactOrder(
    request<ContactOrder>(
      `/cases/${caseId}/contact-orders/${contactOrderId}/steps/${stepId}/result`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),
    stepId,
  )

export const executeContactOrderStepNow = (
  caseId: string,
  contactOrderId: string,
  stepId: string,
  payload: ExecuteContactOrderStepNowPayload = {},
) =>
  stepFromContactOrder(
    request<ContactOrder>(
      `/cases/${caseId}/contact-orders/${contactOrderId}/steps/${stepId}/execute-now`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),
    stepId,
  )

export const markManualLetterStepDueNow = (
  caseId: string,
  contactOrderId: string,
  stepId: string,
) =>
  request<ContactOrderStep>(
    `/appointment-contact-plans/${contactOrderId}/steps/${stepId}/mark-letter-due-now?case_id=${caseId}`,
    {
      method: "POST",
    },
  )

export const cancelContactOrderStep = (caseId: string, contactOrderId: string, stepId: string) =>
  stepFromContactOrder(
    request<ContactOrder>(
      `/cases/${caseId}/contact-orders/${contactOrderId}/steps/${stepId}/cancel`,
      {
        method: "POST",
      },
    ),
    stepId,
  )

export const cancelContactOrderStepWithOrder = (
  caseId: string,
  contactOrderId: string,
  stepId: string,
) =>
  orderAndStepFromContactOrder(
    request<ContactOrder>(
      `/cases/${caseId}/contact-orders/${contactOrderId}/steps/${stepId}/cancel`,
      {
        method: "POST",
      },
    ),
    stepId,
  )

export const skipContactOrderStepWaitWithOrder = (
  caseId: string,
  contactOrderId: string,
  stepId: string,
) =>
  orderAndStepFromContactOrder(
    request<ContactOrder>(
      `/cases/${caseId}/contact-orders/${contactOrderId}/steps/${stepId}/skip-wait`,
      {
        method: "POST",
      },
    ),
    stepId,
  )

export const testContactOrderStep = (
  caseId: string,
  contactOrderId: string,
  stepId: string,
  payload: TestContactOrderStepPayload,
) =>
  request<TestContactOrderStepResponse>(
    `/cases/${caseId}/contact-orders/${contactOrderId}/steps/${stepId}/test`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  )

export const cancelContactOrder = (caseId: string, contactOrderId: string) =>
  request<ContactOrder>(`/cases/${caseId}/contact-orders/${contactOrderId}/cancel`, {
    method: "POST",
  })

export const cancelContactOrdersBatch = async (
  items: ContactOrderBatchCancelItem[],
): Promise<ContactOrderBatchCancelResponse> => {
  const failed: ContactOrderBatchCancelResponse["failed"] = []
  const requestItems = items
    .map((item) => {
      const contactOrderId = stringValue(item.contact_order_id)
      const caseId = stringValue(item.case_id)

      if (!contactOrderId) return null

      if (!caseId) {
        failed.push({
          contact_order_id: contactOrderId,
          detail: "Missing case_id",
        })

        return null
      }

      return {
        case_id: caseId,
        contact_order_id: contactOrderId,
      }
    })
    .filter((item): item is { case_id: string; contact_order_id: string } => Boolean(item))

  if (!requestItems.length) return { canceled: [], skipped: [], failed }

  const response = await request<ContactOrderBatchCancelResponse>("/contact-orders/batch/cancel", {
    method: "POST",
    body: JSON.stringify({ items: requestItems }),
  })

  return {
    canceled: response.canceled ?? [],
    skipped: response.skipped ?? [],
    failed: [...failed, ...(response.failed ?? [])],
  }
}

export const completeContactOrdersBatch = async (
  items: ContactOrderBatchCancelItem[],
): Promise<ContactOrderBatchCompleteResponse> => {
  const failed: ContactOrderBatchCompleteResponse["failed"] = []
  const requestItems = items
    .map((item) => {
      const contactOrderId = stringValue(item.contact_order_id)
      const caseId = stringValue(item.case_id)

      if (!contactOrderId) return null

      if (!caseId) {
        failed.push({
          contact_order_id: contactOrderId,
          detail: "Missing case_id",
        })

        return null
      }

      return {
        case_id: caseId,
        contact_order_id: contactOrderId,
      }
    })
    .filter((item): item is { case_id: string; contact_order_id: string } => Boolean(item))

  if (!requestItems.length) return { completed: [], skipped: [], failed }

  const response = await request<ContactOrderBatchCompleteResponse>(
    "/contact-orders/batch/complete",
    {
      method: "POST",
      body: JSON.stringify({ items: requestItems }),
    },
  )

  return {
    completed: response.completed ?? [],
    skipped: response.skipped ?? [],
    failed: [...failed, ...(response.failed ?? [])],
  }
}
